// Shared auth-redirect logic for every editor surface (canvas, preview
// when it needs auth, future embeds). Each surface's entry module used
// to reimplement this three-way decision inline:
//
//   1. bare `/` without a project → stays on `/` if authed, else `/signin`
//   2. unauthed on a non-auth page → `/signin` (carrying the query)
//   3. authed on an auth page      → `/` (UNLESS a trusted
//      cross-origin handoff is requested via `?redirect=` — see below)
//
// plus a separate "the saved token expired, log out and bounce" flow
// when a project-data fetch comes back with an auth-flavored error.

import {
  isTrustedHandoffTarget,
  encodeHandoffPayload,
  appendHandoffFragment
} from './authHandoff.js'
import { classifyHandoffTarget } from './handoffTargets.js'

// FA204 / frank trap — DEFAULT_AUTH_PATHS/_maybeMintHandoffFromGuard/
// UNAUTH_ERROR_RE used to live at module scope, referenced bare from the
// exports below. Comments elsewhere in this package ("same class as
// authGuard.js's DEFAULT_AUTH_PATHS") point back to this file as the
// canonical example, so per-export self-contained copies below are the
// canonical shape now too (grep confirmed no external consumer ever
// imported the module-scope `DEFAULT_AUTH_PATHS` export — only comments
// referenced it by name).

/**
 * Apply the three-way redirect rule and report back what was decided.
 * Mutates `history` when a redirect fires. Safe to call before any data
 * load — callers gate project fetches on `{ authed }` and render signed-
 * out chrome when `authed === false`.
 *
 * @param {object} req
 * @param {object} req.sdk — SDK instance (needs `getAuthToken()`)
 * @param {boolean} req.hasProjectParams — true when owner+key resolved
 *   from URL params or host/path (tells us to skip the bare-`/` redirect)
 * @param {Location} [req.loc=window.location]
 * @param {Set<string>} [req.authPaths=DEFAULT_AUTH_PATHS]
 * @returns {{ authed: boolean, onAuthPage: boolean, redirected: boolean }}
 */
export function applyAuthGuard({
  sdk,
  hasProjectParams = false,
  loc = typeof window !== 'undefined' ? window.location : null,
  authPaths
} = {}) {
  const defaultAuthPaths = new Set([
    '/signin',
    '/signup',
    '/signup-confirmation',
    '/reset-password',
    '/waitlist'
  ])

  const authed = !!sdk?.getAuthToken?.()
  if (!loc) return { authed, onAuthPage: false, redirected: false }

  // Fallback moved from the destructuring default to a body statement —
  // frank's `to-json` rewrites the module-scope `DEFAULT_AUTH_PATHS` free
  // var correctly EITHER way (it's promoted to globalScope, verified: it
  // round-trips as a tagged `Set`), but rewriting it AT the destructuring
  // default position changes the emitted parameter-list TEXT, which trips
  // the `functions`/`methods` arity-contract check as `signature-drift`
  // even though the arity itself never changed. A body reference doesn't
  // touch the signature.
  const resolvedAuthPaths = authPaths || defaultAuthPaths
  const path = loc.pathname
  const onAuthPage = resolvedAuthPaths.has(path)
  let redirected = false

  // When an already-authed user lands on /signin?redirect=<trusted-target>
  // (a consumer surface like gazer/preview redirecting in to grab a
  // handoff), short-circuit the dashboard bounce and mint the fragment
  // immediately. Without this, the `authed && onAuthPage` rule below
  // rewrites /signin → / before signin.onSubmit's handoff branch ever runs.
  const maybeMintHandoffFromGuard = (sdkArg, locArg) => {
    if (!locArg || typeof window === 'undefined') return false
    const redirectParam = new URLSearchParams(locArg.search).get('redirect')
    if (!redirectParam || !isTrustedHandoffTarget(redirectParam)) return false
    const accessToken = typeof sdkArg?.getAuthToken === 'function' ? sdkArg.getAuthToken() : null
    if (!accessToken) return false
    let refreshToken = ''
    let expiresAt = 0
    let expiresIn = 0
    try {
      refreshToken = window.localStorage.getItem('symbols_refresh_token') || ''
      expiresAt = parseInt(window.localStorage.getItem('symbols_expires_at') || '0', 10) || 0
      expiresIn = parseInt(window.localStorage.getItem('symbols_expires_in') || '0', 10) || 0
    } catch {
      /* localStorage unavailable — fragment still works with access token alone */
    }
    const payload = encodeHandoffPayload({ accessToken, refreshToken, expiresAt, expiresIn })
    const target = appendHandoffFragment(redirectParam, payload)
    window.location.replace(target)
    return true
  }

  if (path === '/' && !hasProjectParams) {
    // Authed users STAY on `/` (the workspace home — /dashboard is retired);
    // signed-out users land on /signin.
    if (!authed) {
      history.replaceState(null, '', '/signin' + loc.search)
      redirected = true
    }
  } else if (!authed && !onAuthPage) {
    // Always land signed-out users on /signin — including when a project
    // URL is requested directly. Project fetches need an auth token, and
    // the editor chrome has its own signed-out states for /signin.
    history.replaceState(null, '', '/signin' + loc.search)
    redirected = true
  } else if (authed && onAuthPage) {
    // Cross-origin handoff short-circuit — see maybeMintHandoffFromGuard.
    if (maybeMintHandoffFromGuard(sdk, loc)) {
      return { authed, onAuthPage, redirected: true }
    }
    // A target that renders a user project needs the server's word first
    // (confirmHandoffTarget, async) — stay on the auth page so signin.js
    // `onInit` can ask and then mint or send the user home.
    const pendingRedirect = new URLSearchParams(loc.search).get('redirect')
    if (pendingRedirect && classifyHandoffTarget(pendingRedirect).verdict === 'confirm') {
      return { authed, onAuthPage, redirected: false }
    }
    history.replaceState(null, '', '/' + loc.search)
    redirected = true
  }

  return { authed, onAuthPage, redirected }
}

/**
 * Is this fetch error the "stale/invalid auth token" variety? Used by
 * callers that want to distinguish a 401 from a genuine 404 so they can
 * run the logout-and-bounce flow instead of rendering "project not found".
 */
export function isAuthError(err) {
  if (!err) return false
  const unauthErrorRe = /(log in|unauthori[sz]ed|401)/i
  const msg = (err.message || '') + ' ' + (err.cause?.message || '')
  return unauthErrorRe.test(msg)
}

/**
 * Logout + history replace to `/signin`. Caller MUST NOT continue the
 * project-load flow after this — typically followed by an early return
 * that renders signed-out chrome.
 */
export async function bounceToSigninOnAuthError(sdk) {
  try {
    await sdk?.logout?.()
  } catch {}
  if (typeof window !== 'undefined') {
    history.replaceState(null, '', '/signin')
  }
}

/**
 * Router-guard factory for per-navigation auth enforcement. Unlike
 * `applyAuthGuard` (which runs once at cold boot), this guard fires on
 * EVERY SPA route change — so an already-running app that SPA-navs to
 * `/signin` while signed-in bounces to `/` without needing
 * per-page `onRender` redirects on each auth page.
 *
 * Usage:
 *   context.routerOptions = { guards: [createAuthRouteGuard({ sdk })] }
 *
 * Guard return convention (smbls router):
 *   - `true` / `undefined`: allow navigation
 *   - `false`: cancel navigation
 *   - `string`: redirect to that path
 *
 * @param {object} opts
 * @param {object} opts.sdk — SDK instance; uses `getAuthToken()` sync
 *   (must be in `_SYNC_PASS`; the core/sdk.js wrapper handles that).
 * @param {string} [opts.signedInDestination='/']
 * @param {Set<string>} [opts.authPaths=DEFAULT_AUTH_PATHS]
 * @returns {(ctx: { pathname: string }) => true | string}
 */
export function createAuthRouteGuard({ sdk, signedInDestination = '/', authPaths } = {}) {
  // See applyAuthGuard above — fallback moved off the destructuring default
  // for the same signature-drift reason.
  const defaultAuthPaths = new Set([
    '/signin',
    '/signup',
    '/signup-confirmation',
    '/reset-password',
    '/waitlist'
  ])
  const resolvedAuthPaths = authPaths || defaultAuthPaths
  return ({ pathname }) => {
    if (!resolvedAuthPaths.has(pathname)) return true
    const authed = !!sdk?.getAuthToken?.()
    return authed ? signedInDestination : true
  }
}
